<template>
  <div class="coupon">
    <div class="coupon--header">
      <div
        v-if="displayName"
        class="s-px-3 s-text-2xl s-font-bold text-truncate"
      >
        {{ displayName }}
      </div>
      <div class="s-mt-2">
        <p>{{usagePeriodTitle}}：</p>
        <time> {{ startToEnd }}</time>
      </div>
    </div>

    <div class="coupon--wrapper">
      <div class="coupon--image">
        <img v-if="displayImage" :src="displayImage" />
      </div>
      <div class="coupon--body">
        <div class="coupon--barcode s-mb-4" v-if="isShowBarcode && couponModel.isRedeemable">
          <p v-if="showBarcodeError" class="s-text-danger">
            barcode 產生錯誤，請聯絡相關人員
          </p>
          <img
            v-else
            :id="barcodeImgId"
            src="@/pages/Dashboard/Coupon/barcode.png"
          />
        </div>

        <div v-if="showCode && coupon.code && !isHideCode && couponModel.isRedeemable" class="coupon--copy-panel">
          <span class="coupon--barcode">
            {{ coupon.code }}
          </span>

          <div
            id="copy-tooltip-target"
            class="coupon--copy-button"
            v-clipboard:copy="coupon.code"
            v-clipboard:success="clipboardSuccessHandler"
          >
            <i :class="copyIconClass" aria-hidden="true"></i>
          </div>

          <b-tooltip
            ref="copyTooltip"
            target="copy-tooltip-target"
            placement="top"
          >
            已複製
          </b-tooltip>
        </div>
      </div>

      <div class="coupon--footer">
        <div class="coupon--line mb-2"></div>
        <div class="s-mb-6 s-mt-3" v-if="showConfirmButton">
          <div class="coupon-confirm mb-1" v-if="couponModel.isRedeemable">
            確認使用後將無法復原，請您確認。
          </div>
          <button
            class="s-btn s-btn-bg-primary s-btn-xs"
            type="button"
            @click="handleConfirm"
            :disabled="false === couponModel.isRedeemable"
          >
            {{ confirmButtonText }}
          </button>
        </div>
        <div class="s-mb-6 s-mt-3" v-if="showUseButton">
          <button
            class="s-btn s-btn-outline-primary s-btn-xs d-block text-truncate"
            type="button"
            @click="handleUse"
            :disabled="false === couponModel.isRedeemable"
          >
            {{ useButtonText }}
          </button>
        </div>
        <div
          v-if="displayDescription"
           class="s-mb-4 s-px-3 coupon--desc"
          style="white-space: pre-line"
        >
          {{ displayDescription }}
        </div>

        <div class="s-mb-6 s-mt-3" v-if="showQrcodeButton">
          <button
            class="s-btn s-btn-bg-primary s-btn-xs"
            type="button"
            @click="handleQrcodeButtonClicked"
            :disabled="false === couponModel.isRedeemable"
          >
            {{ qrcodeButtonText }}
          </button>
        </div>
      </div>
    </div>
    <b-modal ref="qrcodeModal" :hide-header="true" :hide-footer="true" :noCloseOnEsc="true" :noCloseOnBackdrop="true">
      <div class="modal-close-button" @click="$refs.qrcodeModal.hide()">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M368 368L144 144M368 144L144 368"/></svg>
      </div>
      <vue-qr v-if="qrcodeData" :text="qrcodeData" style="width: 100%"></vue-qr>
    </b-modal>
  </div>
</template>

<script>
import "bootstrap-vue/dist/bootstrap-vue.css";
import Vue from "vue";
import VueClipboard from "vue-clipboard2";
import VueQr from "vue-qr"
import bwipjs from "bwip-js";
import { format } from "date-fns"
import { mapActions, mapGetters, mapState } from "vuex"
import couponApi from "@/apis/liff/v2/coupon";
import * as consts from "@/consts";
import Coupon from "@/models/Coupon.model"
Vue.use(VueClipboard);

export default {
  components: {
    VueQr,
  },
  props: {
    coupon: {
      type: Object,
      default: () => {
        return {
          code: null,
          redeem_code_bundle: {},
        };
      },
    },
    confirmFromOutside: {
      type: Boolean,
      default: false,
    },
    redeemFromOutside: {
      type: Boolean,
      default: false,
    },
    usagePeriodTitle: {
      type: String,
      default: "使用期限",
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    showCode: {
      type: Boolean,
      default: true,
    },
    showQrcodeButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      consts,
      canvasElement: null,
      showBarcodeError: false,
      copyIconClass: "fa fa-copy",
    };
  },

  computed: {
    ...mapGetters({ getModuleConfig: 'liffModule/getConfig' }),
    ...mapState('liffAuth', ['user']),
    couponModel() {
      return new Coupon(this.coupon)
    },
    qrcodeButtonText() {
      const actionText = this.getModuleConfig('liff_coupon', 'detail.qrcode.button_text') || "顯示優惠條碼"
      return this.couponModel.isRedeemable ? actionText : `${actionText} (${this.couponModel.unredeemableReason})`
    },
    qrcodeData() {
      const configuredData = this.getModuleConfig('liff_coupon', 'detail.qrcode.data')
      if (undefined === configuredData) return null

      let parsedData
      try {
        parsedData = JSON.parse(configuredData)
      } catch (error) {
        console.warn('解析 qrcode data 失敗:', error)
        return null
      }

      function isObject(value) {
        return typeof value === 'object' && value !== null
      }

      if (!isObject(parsedData)) {
        console.warn('qrcode data 不是 object 格式')
        return null
      }

      Object.keys(parsedData).forEach(key => {
        parsedData[key] = this.parseVariable(parsedData[key]) || ""
      })

      return JSON.stringify(parsedData)
    },
    barcodeImgId() {
      return `coupon-barcode-${this.coupon.id}`;
    },
    showConfirmButton() {
      return this.coupon?.confirm_type !== consts.REDEEM_CODE_BUNDLE_CONFIRM_TYPES.NONE
        && !this.previewMode
    },
    confirmButtonText() {
      const actionText = "確認使用"
      return this.couponModel.isRedeemable ? actionText : `${actionText} (${this.couponModel.unredeemableReason})`
    },
    startToEnd() {
      let startAt = this.coupon?.start_at ?? this.coupon?.redeem_code_bundle?.start_at ?? false;
      let endAt = this.coupon?.end_at ?? this.coupon?.redeem_code_bundle?.end_at ?? false;

      if (!startAt && !endAt) return "";

      let startAtText = startAt
        ? format(new Date(startAt), "yyyy/MM/dd")
        : "即日起";
      let endAtText = endAt
        ? format(new Date(endAt), "yyyy/MM/dd")
        : "無期限";

      return `${startAtText} ~ ${endAtText}`;
    },
    displayName() {
      if (this.coupon.redeem_code_bundle) {
        return this.coupon.redeem_code_bundle.name;
      }
      return this.coupon.name ?? "";
    },
    displayDescription() {
      if (this.coupon.redeem_code_bundle) {
        return this.coupon.redeem_code_bundle.description;
      }
      return this.coupon.description ?? "";
    },
    displayImage() {
      if (this.coupon.redeem_code_bundle) {
        return this.coupon.redeem_code_bundle.img_url;
      }
      return this.coupon.img_url ?? "";
    },
    displayButtonText() {
      if (this.coupon.redeem_code_bundle) {
        return this.coupon.redeem_code_bundle.button_text;
      }
      return this.coupon.button_text ?? "";
    },
    useButtonText() {
      let actionText;
      if (this.coupon.redeem_code_bundle) {
        actionText = this.coupon.redeem_code_bundle.button_text;
      } else {
        actionText = this.coupon.button_text ?? "";
      }
      return this.couponModel.isRedeemable ? actionText : `${actionText} (${this.couponModel.unredeemableReason})`
    },
    showUseButton() {
      if (this.coupon.redeem_code_bundle) {
        return this.coupon.redeem_code_bundle.is_show_button;
      }
      return this.coupon.is_show_button ?? false;
    },
    isHideCode() {
      return !!(this.coupon.redeem_code_bundle?.is_hide_code || this.coupon.is_hide_code)
    },
    isShowBarcode() {
      return !!this.coupon.code
        && !!(this.coupon.redeem_code_bundle?.is_show_barcode || this.coupon.is_show_barcode)
    },
  },

  mounted() {
    if (this.isShowBarcode) {
      this.generateBarcode();
    }
  },

  methods: {
    format,
    ...mapActions('liffLoading', ['showLoading', 'hideLoading']),
    generateBarcode() {
      if (!this.canvasElement) {
        this.canvasElement = document.createElement("canvas");
      }

      try {
        bwipjs.toCanvas(this.canvasElement, {
          bcid: this.getBarcdoeType(), // Barcode type
          text: this.coupon.code,
          scale: 2, // 3x scaling factor
          height: 10, // Bar height, in millimeters
          includetext: !this.isHideCode, // Show human-readable text
          textxalign: "center", // Always good to set this
        });

        let barcodeDom = document.getElementById(this.barcodeImgId);

        if (!barcodeDom || !this.barcodeImgId) {
          this.showBarcodeError = true;
          return;
        }

        barcodeDom.src = this.canvasElement.toDataURL("image/png");
        this.showBarcodeError = false;
      } catch (e) {
        console.log(e);
        this.showBarcodeError = true;
      }
    },
    getBarcdoeType() {
      switch (this.coupon.redeem_code_bundle?.barcode_type) {
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.CODE_128:
          return "code128";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.CODE_39:
          return "code39";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.CODE_39_WITH_CHECKSUM:
          return "code39ext";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.PZN:
          return "pzn";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.EAN_13:
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.JAN: // another name for the EAN-13
          return "ean13";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.EAN_8:
          return "ean8";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.ISBN_13:
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.ISBN_10:
          return "isbn";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.ISSN:
          return "issn";
        case consts.REDEEM_CODE_BUNDLE_BARCODE_TYPES.UPC_A:
          return "upca";
      }
    },
    handleConfirm() {
      if (this.confirmFromOutside) {
        this.$emit("confirmFromOutside", this.coupon);
        return;
      }

      this.showConfirmModal();
    },
    handleUse() {
      if (this.coupon?.button_url) {
        const buttonUrl = this.coupon?.button_url.replace(
          "{$code}",
          this.coupon.code
        );

        if (this.coupon?.button_url_external) {
          window.open(buttonUrl, "_blank").focus();
        } else {
          window.location.href = buttonUrl;
        }
      }
    },
    showConfirmModal() {
      let confirmInput = this.coupon?.redeem_code_bundle?.confirm_type === consts.REDEEM_CODE_BUNDLE_CONFIRM_TYPES.BRANCH_CODE
        ? {
            input: "text",
            inputPlaceholder: "請輸入店號",
            animation: false,
            preConfirm: (value) => {
              if (!value) {
                return this.$swal.showValidationMessage("請輸入店號")
              }

              return new Promise((resolve) => {
                this.coupon.branch_code = value;
                resolve();
              });
            },
          }
        : {};

      this.$swal
        .fire({
          title: "確定要使用嗎？",
          text: "確認使用後將無法復原",
          confirmButtonText: "確認使用",
          cancelButtonText: "取消",
          showCancelButton: true,
          reverseButtons: true,
          showLoaderOnConfirm: true,
          ...confirmInput,
        })
        .then((result) => {
          if (result.value) {
            this.redeemCoupon();
          }
        });
    },
    async redeemCoupon() {
      if (this.redeemFromOutside) {
        this.$emit("redeemFromOutside", this.coupon);
        return;
      }

      try {
        this.showLoading()

        await couponApi.redeemCoupon(this.coupon);

        this.$swal.fire({ title: "完成使用", type: "success" });
        this.$emit("confirmed");
      } catch (error) {
        console.log("error", error);
        this.$swal.fire({
          title: error.response?.data?.message || "使用失敗",
          type: "error",
        })
      } finally {
        this.hideLoading()
      }
    },
    clipboardSuccessHandler() {
      this.$refs.copyTooltip.$emit("open");
      this.copyIconClass = "fa fa-check";
      setTimeout(() => {
        this.copyIconClass = "fa fa-copy";
        this.$refs.copyTooltip.$emit("close");
      }, 1000);
    },
    handleQrcodeButtonClicked() {
      this.$refs.qrcodeModal.show()
    },
    parseVariable(value) {
      if (value === "{$mobile_phone}") {
        return this.user.mobile_phone
      }
      if (value === "{$coupon.code}") {
        return this.coupon.code
      }
      if (value.startsWith("{$extra.")) {
        return this.user.extra[value.replace("{$extra.", "").replace("}", "")]
      }
      return value
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../node_modules/font-awesome/css/font-awesome.min.css";
.coupon {

  display: flex;
  flex-direction: column;
  text-align: center;

  border-radius: 10px;
  background-size: 100% 100% ;
  background-repeat: no-repeat;
  background-image: radial-gradient(circle at 50% 101%, rgba(white,0) 1.5em, white (1.6em));
  background-position: top left, top right;
  //filter: drop-shadow(0 2px 8px rgba(black,.3));
  padding-bottom: 1.5em;
  height: 100%;

  &--header {
    background: var(--liff-header-color);
    color: var(--liff-header-text-color);
    padding: 15px 10px 10px 10px;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
    border-top: 1px solid var(--liff-header-text-color);
    border-left: 1px solid var(--liff-header-text-color);
    border-right: 1px solid var(--liff-header-text-color);
  }

  &--barcode {
    font-weight: 400;
    font-size: 16px;
    line-height: 18px;
  }

  &--wrapper{
    overflow-y: scroll;
  }

  &--image {
    margin: 12px;

    img {
      width: 100%;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
  }

  &--body {
    padding: 0 30px;
    margin: 0 12px 12px 12px;
  }

  &--footer {
    padding: 0 24px 12px;

    .coupon-confirm {
      color: #ea0000;
      font-size: 14px;
    }
  }

  &--copy-panel {
    background: #f2f2f7;
    border-radius: 10px;
    padding: 12px 8px;
    position: relative;
  }

  &--copy-button {
    background: rgba(102, 153, 204, 0.03);
    border: 1px solid rgba(102, 153, 204, 0.6);
    color: #3973ac;
    padding: 4px 16px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    position: absolute;
    right: 8px;
    top: 10px;
  }

  &--line {
    border: 1px dashed #dadada;
  }

  button {
    font-size: 16px;

    &.s-btn-bg-primary {
      background: var(--liff-button-color);
      color: var(--liff-button-text-color);
    }
  }
}

.modal-close-button {
  position: absolute;
  top: -64px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: white;
}

::v-deep .modal-content {
  margin-top: 80px;
  position: relative;
}
</style>
<style scoped>
.coupon--desc {
  text-align: left;
  font-size: 0.9rem;
  box-shadow: rgb(from var(--s-primary) r g b / 0.5) 0 2px 8px 0;
  border-radius: 5px;
  padding: 10px 15px;
}
</style>
